import { getAccessToken } from '@/lib/api-client'
import {
  isSupabaseConfigured,
  supabase,
  supabaseUrl,
  supabaseAnonKey,
} from '@/lib/supabase-client'

export type AccessStatus = {
  allowed: boolean
  isAdmin: boolean
  error?: string
  /**
   * Hatanın kendiliğinden düzelmesi beklenebilir mi? 5xx ve ağ hataları geçicidir;
   * 4xx (401/403/404) kalıcı yetki/kimlik hatasıdır ve son bilinen admin güvenini
   * uzatmak için gerekçe DEĞİLDİR (#492).
   */
  isTransient?: boolean
}

// Direct fetch helper to bypass potential Supabase client issues
async function fetchAllowedEmailDirect(
  normalizedEmail: string
): Promise<{ data: { is_admin: boolean } | null; error: string | null; isTransient?: boolean }> {
  if (!supabaseUrl || !supabaseAnonKey) {
    return { data: null, error: 'Supabase URL veya anon key eksik.', isTransient: false }
  }

  // Get the access token from in-memory cache (api-client)
  const accessToken = await getAccessToken()

  const headers: Record<string, string> = {
    apikey: supabaseAnonKey,
    'Content-Type': 'application/json',
  }

  // Use authenticated token if available (required for RLS)
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`
  } else {
    headers['Authorization'] = `Bearer ${supabaseAnonKey}`
  }

  try {
    const url = `${supabaseUrl}/rest/v1/allowed_emails?email=eq.${encodeURIComponent(normalizedEmail)}&select=is_admin`

    const response = await fetch(url, { headers })

    if (!response.ok) {
      const errorText = await response.text()
      console.warn('[fetchAccessStatus] API error:', response.status, errorText)
      return {
        data: null,
        error: `API hatası: ${response.status}`,
        // 429 (rate limit) 4xx aralığındadır ama kalıcı DEĞİLDİR — pencere dolunca
        // düzelir. Kalıcı sayılırsa gerçek admin anında yetkisini kaybeder (#492 review).
        isTransient: response.status >= 500 || response.status === 429,
      }
    }

    const result = await response.json()

    // Result is an array, get first item
    if (Array.isArray(result) && result.length > 0) {
      return { data: { is_admin: result[0].is_admin }, error: null }
    }

    return { data: null, error: null }
  } catch (err) {
    // Ağ/CORS hatası: sunucuya ulaşılamadı, geçici kabul edilir.
    console.warn('[fetchAccessStatus] Fetch error:', err)
    return {
      data: null,
      error: err instanceof Error ? err.message : 'Bilinmeyen hata',
      isTransient: true,
    }
  }
}

async function performAccessStatusFetch(normalizedEmail: string): Promise<AccessStatus> {
  try {
    // Use direct fetch to bypass potential Supabase client issues
    const { data, error, isTransient } = await fetchAllowedEmailDirect(normalizedEmail)

    if (error) {
      console.warn('[fetchAccessStatus] Yetki kontrolünde hata (kullanıcı oturumdan atılmayacak):', error)
      return { allowed: false, isAdmin: false, error, isTransient: isTransient ?? false }
    }

    if (!data) {
      return { allowed: false, isAdmin: false }
    }

    return {
      allowed: true,
      isAdmin: Boolean(data.is_admin),
    }
  } catch (caughtError) {
    console.warn('[fetchAccessStatus] Yetki kontrolü başarısız oldu:', caughtError)
    return {
      allowed: false,
      isAdmin: false,
      error: caughtError instanceof Error ? caughtError.message : 'Bilinmeyen hata',
      isTransient: true,
    }
  }
}

// In-flight dedup + kısa TTL cache.
// Aynı login/refresh anında birden fazla bağımsız listener (App.tsx, user-profile-provider,
// login.tsx, auth-callback.tsx) aynı email için fetchAccessStatus çağırıyor. Cache olmadan
// her biri ayrı bir Supabase allowed_emails sorgusu üretiyordu (login 3×, refresh 4×).
// Bu cache, aynı email için kısa pencerede tüm çağrıları tek isteğe indirir.
type AccessCacheEntry = { promise: Promise<AccessStatus>; ts: number }
const accessStatusCache = new Map<string, AccessCacheEntry>()
const ACCESS_STATUS_TTL_MS = 10_000

/**
 * Yetki-kontrol cache'ini temizler. Kullanıcı değiştiğinde (SIGNED_OUT) çağrılmalı ki
 * bir sonraki kullanıcı stale bir sonuç almasın.
 */
export function clearAccessCache(): void {
  accessStatusCache.clear()
}

export async function fetchAccessStatus(email: string | null | undefined): Promise<AccessStatus> {
  if (!email) {
    return { allowed: false, isAdmin: false, error: 'E-posta adresi eksik.' }
  }

  if (!isSupabaseConfigured() || !supabase) {
    return {
      allowed: false,
      isAdmin: false,
      error: 'Supabase yapılandırması eksik görünüyor.',
    }
  }

  const normalizedEmail = email.trim().toLowerCase()
  if (!normalizedEmail) {
    return { allowed: false, isAdmin: false, error: 'E-posta adresi geçersiz.' }
  }

  const now = Date.now()
  const cached = accessStatusCache.get(normalizedEmail)
  if (cached && now - cached.ts < ACCESS_STATUS_TTL_MS) {
    return cached.promise
  }

  const promise = performAccessStatusFetch(normalizedEmail)
  accessStatusCache.set(normalizedEmail, { promise, ts: now })

  // Hatalı sonuçları cache'leme: geçici network/API hatası bir sonraki çağrıda
  // yeniden denenebilmeli (RequireAuth geçici hatada oturumu koruyup tekrar dener).
  void promise
    .then((result) => {
      if (result.error) {
        accessStatusCache.delete(normalizedEmail)
      }
    })
    .catch(() => {
      accessStatusCache.delete(normalizedEmail)
    })

  return promise
}
